import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "@/lib/api";
import { QRCodeSVG } from "qrcode.react";
import { useLanguage } from "@/context/LanguageContext";
import { Languages, Download, Crown, ShieldCheck } from "lucide-react";
import SEO from "@/components/SEO";

const LOGO = "https://customer-assets.emergentagent.com/job_beauty-hub-manage/artifacts/h3pmooxo_image.png";

/* ========================================================
   Decorative SVG shapes (gold sweep + burgundy wave bottom)
   ========================================================= */
function CornerSweep({ side = "top-left", gold = "#D4A855", dark = "#2B0E12" }) {
  // Gold swoosh at top-left / bottom-right
  if (side === "top-left") {
    return (
      <svg className="absolute top-0 start-0 pointer-events-none" width="160" height="130" viewBox="0 0 160 130" fill="none">
        <path d="M0 0 Q60 30 160 0 L160 30 Q60 60 0 30 Z" fill={gold} opacity="0.95" />
        <path d="M0 30 Q60 70 160 30 L160 60 Q60 95 0 60 Z" fill={dark} opacity="0.9" />
      </svg>
    );
  }
  return null;
}

function BottomWave({ color = "#7A1528", gold = "#D4A855" }) {
  return (
    <svg className="absolute bottom-0 start-0 w-full pointer-events-none" height="120" viewBox="0 0 400 120" preserveAspectRatio="none">
      <path d="M0 60 Q100 120 220 80 T400 60 L400 120 L0 120 Z" fill={color} />
      <path d="M0 80 Q100 140 220 100 T400 80" stroke={gold} strokeWidth="2" fill="none" opacity="0.9" />
    </svg>
  );
}

/* Laurel wreath around the points number */
function LaurelWreath({ children, color = "#7A1F3D" }) {
  return (
    <div className="relative inline-flex items-center justify-center px-6 py-1">
      {/* Left laurel */}
      <svg className="absolute start-0 top-1/2 -translate-y-1/2" width="32" height="52" viewBox="0 0 32 52" fill="none">
        <path d="M28 26 Q10 4 4 14 Q14 18 24 30" stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round" />
        <path d="M28 26 Q10 48 4 38 Q14 34 24 22" stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round" />
        <ellipse cx="8" cy="18" rx="4" ry="2" transform="rotate(-30 8 18)" fill={color} opacity="0.85" />
        <ellipse cx="5" cy="26" rx="4" ry="2" transform="rotate(0 5 26)" fill={color} opacity="0.85" />
        <ellipse cx="8" cy="34" rx="4" ry="2" transform="rotate(30 8 34)" fill={color} opacity="0.85" />
        <ellipse cx="16" cy="10" rx="3.5" ry="1.8" transform="rotate(-50 16 10)" fill={color} opacity="0.7" />
        <ellipse cx="16" cy="42" rx="3.5" ry="1.8" transform="rotate(50 16 42)" fill={color} opacity="0.7" />
      </svg>
      {/* Right laurel (mirrored) */}
      <svg className="absolute end-0 top-1/2 -translate-y-1/2" width="32" height="52" viewBox="0 0 32 52" fill="none" style={{ transform: "translateY(-50%) scaleX(-1)" }}>
        <path d="M28 26 Q10 4 4 14 Q14 18 24 30" stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round" />
        <path d="M28 26 Q10 48 4 38 Q14 34 24 22" stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round" />
        <ellipse cx="8" cy="18" rx="4" ry="2" transform="rotate(-30 8 18)" fill={color} opacity="0.85" />
        <ellipse cx="5" cy="26" rx="4" ry="2" transform="rotate(0 5 26)" fill={color} opacity="0.85" />
        <ellipse cx="8" cy="34" rx="4" ry="2" transform="rotate(30 8 34)" fill={color} opacity="0.85" />
        <ellipse cx="16" cy="10" rx="3.5" ry="1.8" transform="rotate(-50 16 10)" fill={color} opacity="0.7" />
        <ellipse cx="16" cy="42" rx="3.5" ry="1.8" transform="rotate(50 16 42)" fill={color} opacity="0.7" />
      </svg>
      <span className="relative z-10">{children}</span>
    </div>
  );
}

/* Metallic tier badge (Silver / Gold / Platinum) */
function TierBadge({ tier = "Silver" }) {
  const t = (tier || "").toLowerCase();
  const config = {
    silver: {
      gradient: "linear-gradient(135deg, #F5F5F5 0%, #C0C0C0 25%, #FFFFFF 50%, #A8A8A8 75%, #E8E8E8 100%)",
      border: "#8A8A8A",
      text: "#2E2E2E",
      label: "SILVER",
      crown: false,
      shadow: "0 4px 12px rgba(150,150,150,0.4)",
    },
    gold: {
      gradient: "linear-gradient(135deg, #FFF4C4 0%, #E8B948 25%, #FFE27A 50%, #C9922F 75%, #F9D56E 100%)",
      border: "#A47419",
      text: "#5C3D06",
      label: "GOLD",
      crown: true,
      shadow: "0 4px 12px rgba(200,150,40,0.45)",
    },
    platinum: {
      gradient: "linear-gradient(135deg, #F2F4F7 0%, #B8BCC4 25%, #FFFFFF 50%, #8E95A0 75%, #DFE3EA 100%)",
      border: "#6F7680",
      text: "#1E2228",
      label: "PLATINUM",
      crown: true,
      shadow: "0 4px 12px rgba(120,128,140,0.45)",
    },
    bronze: {
      gradient: "linear-gradient(135deg, #F3D5B5 0%, #C9803C 50%, #E8A869 100%)",
      border: "#8A5421",
      text: "#3E1F05",
      label: "BRONZE",
      crown: false,
      shadow: "0 4px 12px rgba(180,100,40,0.4)",
    },
  }[t] || {
    gradient: "linear-gradient(135deg, #F5F5F5 0%, #C0C0C0 50%, #E8E8E8 100%)",
    border: "#8A8A8A", text: "#2E2E2E", label: (tier || "").toUpperCase(), crown: false, shadow: "0 4px 12px rgba(150,150,150,0.4)",
  };
  return (
    <div
      className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-md font-bold tracking-wider text-sm"
      style={{
        background: config.gradient,
        border: `1.5px solid ${config.border}`,
        color: config.text,
        boxShadow: `${config.shadow}, inset 0 1px 2px rgba(255,255,255,0.6), inset 0 -1px 2px rgba(0,0,0,0.08)`,
        minWidth: "120px",
      }}
      data-testid={`tier-badge-${t}`}
    >
      {config.crown && <Crown size={13} strokeWidth={2.5} />}
      {config.label}
    </div>
  );
}

/* Per-tier theme */
function themeFor(tier) {
  const t = (tier || "").toLowerCase();
  if (t === "gold") {
    return {
      bg: "linear-gradient(135deg, #FFF9EB 0%, #FAE8B6 50%, #FFF3D0 100%)",
      topDarkBand: "#1C1310",
      bottomWaveColor: "#7A1528",
      goldAccent: "#C9922F",
      nameColor: "#1F1915",
      labelColor: "#8C1C2A",
      goldText: "#A47419",
    };
  }
  if (t === "platinum") {
    return {
      bg: "linear-gradient(135deg, #FFFFFF 0%, #ECEEF2 45%, #FFFFFF 100%)",
      topDarkBand: "#D7DCE3",
      bottomWaveColor: "#7A1528",
      goldAccent: "#7A1F3D",
      nameColor: "#1A1E24",
      labelColor: "#8C1C2A",
      goldText: "#8C6A2A",
      pattern: true,
    };
  }
  // silver (default)
  return {
    bg: "linear-gradient(135deg, #FFFFFF 0%, #F5F5F5 50%, #FFFFFF 100%)",
    topDarkBand: "transparent",
    bottomWaveColor: "#7A1528",
    goldAccent: "#D4A855",
    nameColor: "#1F1915",
    labelColor: "#8C1C2A",
    goldText: "#7A1F3D",
  };
}

export default function MembershipCard() {
  const { customerId } = useParams();
  const { t, lang, toggleLang } = useLanguage();
  const [card, setCard] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_BASE}/public/card/${customerId}`);
        setCard(r.data);
      } catch { setErr(true); }
    })();
  }, [customerId]);

  const qrValue = `${window.location.origin}/card/${customerId}`;
  const downloadCard = () => window.print();

  if (err) return <div className="min-h-screen flex items-center justify-center text-stone-500">Not found</div>;
  if (!card) return <div className="min-h-screen flex items-center justify-center text-stone-500">{t("loading")}</div>;

  const theme = themeFor(card.membership_tier);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-[#FBF5F1]" data-testid="membership-card-page">
      <SEO title={lang === "ar" ? "بطاقة العضوية" : "Membership Card"} noindex={true} lang={lang} />
      <div className="w-full max-w-sm flex justify-end mb-3">
        <button onClick={toggleLang} className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-stone-300 text-stone-700 text-sm hover:border-[#7A1F3D] transition">
          <Languages size={14} />{lang === "ar" ? "EN" : "ع"}
        </button>
      </div>

      {/* THE CARD */}
      <div
        className="relative w-full max-w-sm rounded-[24px] overflow-hidden shadow-2xl"
        data-testid="card-visual"
        style={{ background: theme.bg, aspectRatio: "9/16", minHeight: "600px" }}
      >
        {/* Platinum diamond pattern overlay */}
        {theme.pattern && (
          <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-[0.07]" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="diamonds" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M20 0 L40 20 L20 40 L0 20 Z" stroke="#1A1E24" strokeWidth="0.5" fill="none" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#diamonds)" />
          </svg>
        )}

        {/* Gold top-left sweep */}
        <CornerSweep side="top-left" gold={theme.goldAccent} dark={theme.topDarkBand} />

        {/* CONTENT */}
        <div className="relative z-10 p-6 pt-7 h-full flex flex-col">
          {/* HEADER: Brand */}
          <div className="flex items-start justify-between">
            <div className="pt-6">
              <div className="font-heading leading-none" style={{ color: theme.goldText, fontSize: "36px", fontWeight: 600 }}>iSaloon</div>
              <div className="mt-1 text-[10px] tracking-[0.3em] text-stone-600 font-semibold">BEAUTY &amp; CARE</div>
            </div>
            <img src={LOGO} alt="iSaloon" className="w-16 h-16 object-contain" />
          </div>

          {/* Name */}
          <div className="mt-10">
            <div className="text-[13px] font-semibold" style={{ color: theme.labelColor }}>{lang === "ar" ? "الاسم" : t("name")}</div>
            <div className="mt-2 text-3xl font-bold" style={{ color: theme.nameColor, letterSpacing: "0.3px" }}>{card.name}</div>
            {/* Gold diamond divider */}
            <div className="flex items-center gap-2 mt-4">
              <span className="h-px flex-1" style={{ background: `linear-gradient(to ${lang === "ar" ? "left" : "right"}, transparent, ${theme.goldAccent})` }} />
              <span style={{ color: theme.goldAccent, fontSize: "10px" }}>◆</span>
              <span className="h-px flex-1" style={{ background: `linear-gradient(to ${lang === "ar" ? "right" : "left"}, transparent, ${theme.goldAccent})` }} />
            </div>
          </div>

          {/* Loyalty Points + Tier */}
          <div className="mt-6 grid grid-cols-5 gap-3 items-center">
            <div className="col-span-3 text-center">
              <div className="text-[13px] font-semibold" style={{ color: theme.labelColor }}>{lang === "ar" ? "نقاط الولاء" : "Loyalty Points"}</div>
              <div className="text-[10px] tracking-[0.25em] text-stone-500 font-semibold mt-0.5">LOYALTY POINTS</div>
              <div className="mt-2">
                <LaurelWreath color={theme.goldAccent}>
                  <span className="text-5xl font-bold" style={{ color: theme.nameColor }}>{card.loyalty_points}</span>
                </LaurelWreath>
              </div>
            </div>
            <div className="col-span-2 text-center">
              <div className="text-[13px] font-semibold mb-3" style={{ color: theme.labelColor }}>{lang === "ar" ? "فئة العضوية" : "Tier"}</div>
              <TierBadge tier={card.membership_tier} />
            </div>
          </div>

          {/* QR Code */}
          <div className="mt-auto flex flex-col items-center">
            <div className="bg-white rounded-xl p-3 shadow-md" style={{ boxShadow: `0 8px 24px rgba(0,0,0,0.15), 0 0 0 2px ${theme.goldAccent}20` }}>
              <QRCodeSVG value={qrValue} size={130} level="M" />
            </div>
            <div className="mt-3 mb-5 flex items-center gap-1.5 text-white text-xs font-semibold z-20 relative" style={{ textShadow: "0 1px 3px rgba(0,0,0,0.6)" }}>
              <ShieldCheck size={13} strokeWidth={2.5} />
              <span>{lang === "ar" ? "امسح للتحقق" : "Scan to verify"}</span>
            </div>
          </div>
        </div>

        {/* Bottom burgundy wave */}
        <BottomWave color={theme.bottomWaveColor} gold={theme.goldAccent} />
      </div>

      <button onClick={downloadCard} data-testid="card-download-btn" className="mt-6 px-6 py-2.5 rounded-md border-2 border-[#9B2950] text-[#9B2950] hover:bg-[#9B2950] hover:text-white transition flex items-center gap-2 font-medium">
        <Download size={14} />{t("membership_card")}
      </button>

      <div className="text-center text-xs text-stone-400 mt-6">{t("developed_by")}</div>
    </div>
  );
}
